import {BreadCrumb, CenteredModal, DropzoneFile, FormInput, TablePaging} from "~/components";
import React, {Fragment, useEffect, useRef, useState} from "react";
import {format} from "date-fns";
import {faEdit, faEye, faTrash, faSave, faCancel} from "@fortawesome/free-solid-svg-icons";
import {Plus, Upload, Download, X, Trash} from "lucide-react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import ChatbotManagementAPI from '~/api/chatbotManagementApi';
import {useFieldArray, useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {Puff} from "react-loader-spinner";

const breadCrumbItem = [
    { text: "Home", to: "/ai-chatbot/administrator", isActive: false },
    { text: "Chatbot Management", to: "", isActive: true },
]
function DataChatbotManagement () {
    const [reloadTable, setReloadTable] = useState(false);
    const [mode, setMode] = useState("View");
    const [showActionModal, setShowActionModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [backupQuestion, setBackupQuestion] = useState("");
    const [isNewQuestion, setIsNewQuestion] = useState(false);
    const [duplicateIndexes, setDuplicateIndexes] = useState([]);
    const [showImportModal, setShowImportModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [dataValidate, setDataValidate] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSaveQuestion, setIsSaveQuestion] = useState(false);
    const [isImportingData, setIsImportingData] = useState(false);
    const [showExportConfirmModal, setShowExportConfirmModal] = useState(false);
    const [progress, setProgress] = useState(0); // 0 → 100
    const [isRunningDownload, setIsRunningDownload] = useState(false);
    const [isShowDownloadArea, setIsShowDownloadArea] = useState(false);
    const timerRef = useRef(null);
    const isFetchingRef = useRef(false);
    const [deleteId, setDeleteId] = useState(0);
    const [showDeleteDupModal, setShowDeleteDupModal] = useState(false);

    const tablePagingConfig = {
        columns: [
            {
                name: 'ID',
                width: '70px',
                className: 'fw-bold',
                key: 'RowNumber'
            },
            {
                name: 'TOPIC',
                width: '100px',
                key: 'Topic'
            },
            {
                name: 'SUB TOPIC',
                width: '150px',
                key: 'SubTopic'
            },
            {
                name: 'CONTENT',
                width: '400px',
                key: 'Content',
                isMoreLess: true
            },
            {
                name: 'REQ QUESTION',
                width: '300px',
                key: 'FirstQuestion',
            },
            {
                name: 'QUESTION COUNT',
                width: '170px',
                key: 'QuestionCount',
            },
            {
                name: 'CREATED DATE',
                width: '150px',
                key: 'CreatedDate',
                template: (row) => {
                    return (
                        row.CreatedDate ? <span>{format(new Date(row.CreatedDate), "dd-MM-yyyy")}</span> : ''
                    )
                }
            },
            {
                name: 'MODIFIED DATE',
                width: '150px',
                key: 'ModifiedDate',
                template: (row) => {
                    return (
                        <span>{formatDate(row.ModifiedDate)}</span>
                    )
                }
            },
            {
                name: 'ACTION',
                width: '90px',
                className: 'text-center',
                headerClassName: 'text-center',
                key: 'Action',
                template: (row) => {
                    return (
                        <div className='d-flex justify-content-center'>
                            <FontAwesomeIcon onClick={()=> handleAction(row.Id, 'View')} icon={faEye} className="me-2 text-primary cursor-pointer"/>
                            <FontAwesomeIcon onClick={()=> handleAction(row.Id, 'Edit')} icon={faEdit} className="me-2 text-primary cursor-pointer"/>
                            <FontAwesomeIcon onClick={()=> handleDeleted(row.Id)} icon={faTrash} className="text-danger cursor-pointer"/>
                        </div>
                    )
                }
            }
        ],
        url: "/chatbot-management/paged",
        tableKey: "TableChatbotManagement",
        extraButtons: [
            <Fragment>
                <button className="btn btn-bt-primary ms-4" type="button" onClick={() => handleAction(0, 'Add')}>Add New
                    <Plus size={15} className="ms-2"/>
                </button>
                <button className="btn btn-bt-primary ms-4" type="button" onClick={() => handleImportData()}>Import Data
                    <Upload size={15} className="ms-2"/>
                </button>
                <button className="btn btn-bt-primary ms-4" type="button" onClick={() => handleExportData()}>Export Data
                    <Download size={15} className="ms-2"/>
                </button>
                <button className="btn btn-bt-primary ms-4" type="button" onClick={() => setShowDeleteDupModal(true)}>Delete Duplicate Question
                    <Trash size={15} className="ms-2"/>
                </button>
            </Fragment>
        ]
    }
    
    const confirmDeleteDuplicate = () => {
        ChatbotManagementAPI.deleteDuplicateQuestion()
            .then(res => {
                if (res.data?.Succeeded){
                    setReloadTable(prev => !prev);
                    toast.success(<span className="fs-9 fw-bold">Delete duplicate question success.</span>);
                }
                else {
                    toast.error(<span className="fs-9 fw-bold">An error occurred while deleting data.</span>);
                }
            })
            .catch(err => {
                toast.error(<span className="fs-9 fw-bold">An error occurred while deleting data.</span>);
            })
            .finally(() => {
                setShowDeleteDupModal(false);
            })
    }

    const handleExportData = () => {
        setShowExportConfirmModal(true);
    }

    function formatDate(dateString) {
        const date = new Date(dateString);

        const isDefaultDate = date.getFullYear() === 1;

        if (isDefaultDate || isNaN(date)) return "";

        return format(date, "dd-MM-yyyy");
    }

    const {
        register,
        handleSubmit,
        reset,
        control,
        getValues,
        formState: { errors }
    } = useForm();

    const {
        register: fileRegister,
        handleSubmit: fileHandleSubmit,
        reset: fileReset,
        formState: { errors: fileError }
    } = useForm();

    const { fields, append, remove } = useFieldArray({
        control,
        name: "Questions"
    });

    const { fields: buttonFields, append: appendButton, remove: removeButton } = useFieldArray({
        control,
        name: "DataChatbot.ButtonLists"
    });

    const handleImportData = () => {
        setSelectedFile(null);
        fileReset({});
        setDataValidate({})
        setShowImportModal(true);
    }


    const handleAction = (id, viewMode) => {
        setMode(viewMode);
        ChatbotManagementAPI.getDataChatBotById(id)
            .then(res => {
                const data = res.data;
                if (data.DataChatbot?.ButtonLists) {
                    try {
                        data.DataChatbot.ButtonLists = JSON.parse(data.DataChatbot.ButtonLists);
                    } catch {}
                }

                if(viewMode === "Add") {
                    const { ButtonLists, ...RestData } = data.DataChatbot;
                    reset({
                        Questions: [
                            {Id: 0, Content: "" }
                        ],
                        DataChatbot : {
                            ButtonLists: [],
                            ...RestData
                        }
                    });
                    setIsNewQuestion(true);
                    setEditingIndex(0);
                }
                else{
                    reset(data);
                }
                setShowActionModal(true);
            })
    }

    const handleDeleted = (id) => {
        setDeleteId(id);
        setShowDeleteModal(true);
    }

    const onSubmitForm = (data) => {
        setIsSubmitting(true);
        if(mode === 'Edit') {
            const {DataChatbot, ...Rest} = data;
            DataChatbot.ButtonLists = JSON.stringify(DataChatbot.ButtonLists);
            ChatbotManagementAPI.updAnswer(DataChatbot)
                .then(res => {
                    res.data?.Succeeded ?
                        toast.success(<span className="fs-9 fw-bold">Update Qna Success.</span>)
                        : toast.error(<span className="fs-9 fw-bold">An error occurred while updating data.</span>);
                    setReloadTable(prev => !prev);
                })
                .catch(err => {
                    toast.error(<span className="fs-9 fw-bold">An error occurred while updating data.</span>);
                })
                .finally(() => {
                    setIsSubmitting(false);
                    handleCloseActionModal();
                })
        }
        else
        {
            data.DataChatbot.ButtonLists  = JSON.stringify(data.DataChatbot.ButtonLists);
            ChatbotManagementAPI.insDataChatbot(data)
                .then(res => {
                    res.data?.Succeeded ?
                        toast.success(<span className="fs-9 fw-bold">Add Qna Success.</span>)
                        : toast.error(<span className="fs-9 fw-bold">An error occurred while adding data.</span>);
                    setReloadTable(prev => !prev);
                })
                .catch(err => {
                    toast.error(<span className="fs-9 fw-bold">An error occurred while adding data.</span>);
                })
                .finally(() => {
                    setIsSubmitting(false);
                    handleCloseActionModal();
                })
        }
    }

    const handleCloseActionModal = () => {
        setShowActionModal(false);
        setEditingIndex(null);
        setBackupQuestion("");
        setIsNewQuestion(false);
        setIsSubmitting(false);
        setIsSaveQuestion(false);
    }

    const validateQuestions = () => {
        const questions = getValues("Questions") || [];

        if(questions.some(q => !q.Content || q.Content.trim() === "")) {
            toast.error(<span className="fs-9 fw-bold">"Question cannot be empty!"</span>);
            return false;
        }

        const contents = questions.map(q => q.Content.trim().toLowerCase());
        const duplicatesIndex = contents.reduce((acc, value, index) => {
            if (contents.indexOf(value) !== index) {
                acc.push(index + 1);
            }
            return acc;
        }, []);
        setDuplicateIndexes(duplicatesIndex);
        if (duplicatesIndex.length > 0) {
            toast.error(
                `Duplicate questions found at positions: ${duplicatesIndex.join(", ")}`
            );
            return false;
        }

        return true;
    };

    const handleAddQuestions = () => {
        if(editingIndex !== null) {
            toast.warning(<span className="fs-9 fw-bold">There is a question being edited, please save or cancel it before adding a new one.</span>);
            return;
        }

        if(!validateQuestions()){
            return;
        }

        append({Id : 0, Content: ""})
        setEditingIndex(fields.length);
        setIsNewQuestion(true);
    }

    const handleAddButton = () => {
        if(buttonFields.length >= 5) {
            toast.warning(<span className="fs-9 fw-bold">Only up to 5 buttons are allowed!</span>);
            return;
        }
        appendButton({ text: "", value: "" });
    }

    const handleEditQuestion = (index) => {
        if(editingIndex !== null){
            toast.warning(<span className="fs-9 fw-bold">"Please save or cancel current question first!"</span>);
            return;
        }

        const questions = getValues("Questions");
        setBackupQuestion(questions[index].Content); // lưu bản gốc

        setEditingIndex(index);
        setIsNewQuestion(!questions[index].Id); // nếu Id == 0 coi như mới
    };

    const handleCancelQuestion = (index) => {
        const questions = getValues("Questions");
        if (isNewQuestion) {
            if(questions.length === 1){
                toast.warning(<span className="fs-9 fw-bold">"You must have at least one question!"</span>);
                return;
            }
            if(mode === "Edit") {
                remove(editingIndex);
                setIsNewQuestion(false);
            }
        } else {
            questions[editingIndex].Content = backupQuestion;
            reset({
                ...getValues(),
                Questions: questions
            });
        }

        setEditingIndex(null);
    };

    const handleRemoveQuestion = (index) => {
        const questions = getValues("Questions");

        if (questions.length === 1) {
            toast.error(<span className="fs-9 fw-bold">"There must be at least one question!"</span>);
            return;
        }

        if(questions[index] && questions[index].Id !== 0)
        {
            ChatbotManagementAPI.deleteQuestion(questions[index].Id)
                .then(res => {
                    if (res.data?.Succeeded){
                        toast.success(<span className="fs-9 fw-bold">Delete question success.</span>);
                        remove(index);
                    }
                    else {
                        toast.error(<span className="fs-9 fw-bold">An error occurred while deleting question.</span>);
                    }
                })
                .catch((err) => {
                    toast.error(<span className="fs-9 fw-bold">An error occurred while deleting question.</span>);
                })
        }
        else {
            remove(index);
        }
    };

    const handleSaveQuestion = () => {
        const questions = getValues("Questions");
        const question = questions[editingIndex];

        if (!question?.Content?.trim()) {
            toast.error(<span className="fs-9 fw-bold">Question cannot be empty!</span>);
            return;
        }
        if(mode === "Edit") {
            setIsSaveQuestion(true);
            var answerId = getValues("DataChatbot.Id");
            ChatbotManagementAPI.insUpdQuestion(question, answerId)
                .then(res => {
                    if(res.data?.Succeeded){
                        toast.success(<span className="fs-9 fw-bold">Save question success.</span>);
                        if(question.Id <= 0)
                        {
                            questions[editingIndex].Id = res.data?.ReturnId;
                            reset({
                                ...getValues(),
                                Questions: questions
                            });
                        }
                    }
                    else {
                        toast.error(<span className="fs-9 fw-bold">An error occurred while saving question.</span>);
                    }
                })
                .catch(err => {
                    toast.error(<span className="fs-9 fw-bold">An error occurred while saving question.</span>);
                })
                .finally(() => {
                    setIsSaveQuestion(false);
                    setEditingIndex(null);
                    setIsNewQuestion(false);
                })
        }
    };

    const onSubmitFormImport = (data) => {
        const { file} = data;
        const formData = new FormData();
        if (file?.[0]) {
            formData.append("file", file[0]);
        }
        ChatbotManagementAPI.validateDataChatbot(formData)
            .then((res) => {
                if(res.data?.Succeeded){
                    setDataValidate(res.data);
                }
                else {
                    toast.error(<span className="fs-9 fw-bold">{res.data?.Messsage}</span>);
                }
            })
            .catch((err) => {
                toast.error(<span className="fs-9 fw-bold">An error occurred while validating your file.</span>);
            })
    }

    useEffect(() => {
        if(!selectedFile){
            setDataValidate({});
        }
    }, [selectedFile])

    const handleConfirmImportData = () => {
        const formData = new FormData();
        if (selectedFile.file) {
            formData.append("file", selectedFile.file);
        }
        setIsImportingData(true);
        ChatbotManagementAPI.importDataChatbot(formData)
            .then(res => {
                if (res.data?.Succeeded){
                    setReloadTable(prev => !prev);
                    toast.success(<span className="fs-9 fw-bold">Import data success.</span>);
                }
                else {
                    toast.error(<span className="fs-9 fw-bold">An error occurred while importing data.</span>);
                }
            })
            .catch((err) => {
                toast.error(<span className="fs-9 fw-bold">An error occurred while importing data.</span>);
            })
            .finally(() => {
                setIsImportingData(false);
                setShowImportModal(false);
            })
    }

    useEffect(() => {
        const jobId = localStorage.getItem("JobId");
        if (!jobId) return;

        setIsRunningDownload(true);
        setIsShowDownloadArea(true);
    }, []);

    useEffect(() => {
        if (!isRunningDownload) return;

        const jobId = localStorage.getItem("JobId");
        if (!jobId) {
            setIsRunningDownload(false);
            setIsShowDownloadArea(false);
            return;
        }

        timerRef.current = setInterval(async () => {
            if (isFetchingRef.current) return;

            isFetchingRef.current = true;
            try {
                const res = await ChatbotManagementAPI.getStatusExport(jobId);
                const data = res?.data;
                if (!data) return;

                setProgress(data.Percent);

                if (data.Percent === 100 && data.Status === "done") {
                    stopPolling();
                }
            } catch {
                stopPolling();
            } finally {
                isFetchingRef.current = false;
            }
        }, 2000);

        return stopPolling;
    }, [isRunningDownload]);

    const stopPolling = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        setIsRunningDownload(false);
    };

    const confirmExportData = () => {
        const jobId = localStorage.getItem("JobId");
        if(jobId) {
            toast.warning(<span className="fs-9 fw-bold">The export is in progress. Please wait a moment...</span>);
            return;
        }
        ChatbotManagementAPI.startExport()
            .then(res => {
                localStorage.setItem("JobId", res.data?.JobId);
                setIsRunningDownload(true);
                setIsShowDownloadArea(true);
            })
            .catch((err) => {
                toast.error(<span className="fs-9 fw-bold">An error occurred while exporting data.</span>);
            })
            .finally(() => {
                setShowExportConfirmModal(false);
            })
    }

    const handleDownloadFile = () => {
        const jobId = localStorage.getItem("JobId");
        if(!jobId) return;
        ChatbotManagementAPI.exportDataChatbot(jobId)
            .then(res => {
                const url = window.URL.createObjectURL(new Blob([res.data]));

                const contentDisposition = res.headers["content-disposition"];
                let fileName = "chatbot-qna.json";

                if (contentDisposition) {
                    const match = contentDisposition.match(/filename="?(.+)"?/);
                    if (match && match[1]) fileName = match[1];
                }

                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", fileName);
                document.body.appendChild(link);
                link.click();

                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
    }

    const handleCloseDownloadArea = () => {
        const jobId = localStorage.getItem("JobId");
        ChatbotManagementAPI.removeJob(jobId)
        localStorage.removeItem("JobId");
        setIsShowDownloadArea(false);
    }

    const confirmDeleteQnA = () => {
        ChatbotManagementAPI.deleteAnswer(deleteId)
            .then(res => {
                if (res.data?.Succeeded){
                    toast.success(<span className="fs-9 fw-bold">Delete QnA Success.</span>);
                    setReloadTable(prev => !prev);
                }
                else {
                    toast.error(<span className="fs-9 fw-bold">An error occurred while deleting QnA.</span>);
                }
            })
            .catch((err) => {
                toast.error(<span className="fs-9 fw-bold">An error occurred while deleting QnA.</span>);
            })
            .finally(() => {
                setShowDeleteModal(false);
            })
    }

    return (
        <Fragment>
            <BreadCrumb breadCrumbItem={breadCrumbItem}/>
            <h2 className="text-bold text-body-emphasis mb-3">Chatbot Management</h2>
            {
                isShowDownloadArea &&
                (
                    <div className="p-2 mb-3 mt-3 border rounded-1">
                        <p className="text-center fs-8-1 fw-bold text-success">Loading File...</p>
                        <div className="progress" style={{ height: "15px" }}>
                            <div
                                className="progress-bar progress-bar-striped progress-bar-animated"
                                role="progressbar"
                                aria-valuenow={progress}
                                aria-valuemin={0}
                                aria-valuemax={100}
                                style={{ width: `${progress}%` }}
                            >
                                {progress}%
                            </div>
                        </div>
                        {
                            progress === 100 ?
                            (
                                <Fragment>
                                    <div className="mt-2 d-flex flex-column align-items-center">
                                        <span className="fs-8-1 mb-1">Download File Here.</span>
                                        <Download size={20} onClick={handleDownloadFile} className="text-success cursor-pointer"/>
                                    </div>
                                    <div className="text-center">
                                        <button className="btn btn-bt-primary" type="button" onClick={() => handleCloseDownloadArea()}>Close
                                            <X size={12} className="ms-2"/>
                                        </button>
                                    </div>
                                </Fragment>
                            )
                            :
                            (
                                <p className="text-center fs-8-1 fw-semibold mt-2">The download file will appear here shortly.</p>
                            )
                        }
                    </div>
                )
            }
            <TablePaging tablePagingConfig={tablePagingConfig} reloadTable={reloadTable}/>
            <CenteredModal
                show={showActionModal}
                onClose={handleCloseActionModal}
                title= {mode + " Qna"}
                formId="formActionDataChatbot"
                isViewMode={mode === "View"}
                closeText={mode === "View" ? "Close" : "Cancel"}
                modalType="modal-lg"
                modalHeight="750px"
                isSubmitting={isSubmitting}
            >
                <form onSubmit={handleSubmit(onSubmitForm)} id="formActionDataChatbot">
                    <div>
                        <div>
                            <div className="row">
                                <div className="col-12 col-sm-6">
                                    <FormInput showHeaderLabel={true}
                                               name = {`DataChatbot.Topic`}
                                               errors={errors}
                                               required={true}
                                               maxLengthText = "max length 500 character"
                                               label={"Assign Topic"}
                                               validation={{...register(`DataChatbot.Topic`, {
                                                       required: "Topic is required."
                                                   })}}
                                               disabled={mode === "View"}
                                               maxLength={500}
                                    />
                                </div>
                                <div className="col-12 col-sm-6">
                                    <FormInput showHeaderLabel={true}
                                               name = {`DataChatbot.SubTopic`}
                                               errors={errors}
                                               required={true}
                                               maxLengthText = "max length 500 character"
                                               label={"Assign SubTopic"}
                                               validation={{...register(`DataChatbot.SubTopic`, {
                                                       required: "SubTopic is required."
                                                   })}}
                                               disabled={mode === "View"}
                                               maxLength={500}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mb-3 nav nav-underline" role="tablist">
                            <div className="nav-item">
                                <a
                                    className="nav-link active"
                                    id="tab-question-tab"
                                    data-bs-toggle="tab"
                                    href="#tab-question"
                                    role="tab"
                                    aria-controls="tab-quesion"
                                    aria-selected="true"
                                >
                                    Chat Questions
                                </a>
                            </div>
                            <div className="nav-item">
                                <a
                                    className="nav-link"
                                    id="tab-answer-tab"
                                    data-bs-toggle="tab"
                                    href="#tab-answer"
                                    role="tab"
                                    aria-controls="tab-answer"
                                    aria-selected="false"
                                >
                                    Chat Answer
                                </a>
                            </div>
                            <div className="nav-item">
                                <a
                                    className="nav-link"
                                    id="tab-button-tab"
                                    data-bs-toggle="tab"
                                    href="#tab-button"
                                    role="tab"
                                    aria-controls="tab-button"
                                    aria-selected="false"
                                >
                                    Chat Buttons
                                </a>
                            </div>
                        </div>
                        <div className="tab-content" style={{height:'425px', overflow: 'auto'}}>
                            <div
                                className="tab-pane fade show active"
                                id="tab-question"
                                role="tabpanel"
                                aria-labelledby="tab-question-tab"
                            >
                                {
                                    mode === "View" ? (
                                        fields.map((item, index) => (
                                            <p className='fs-8-1 fw-semibold mb-2'
                                               key={index}
                                            >{item.Content}</p>
                                        ))
                                    ) : (
                                        fields.map((item, index) => (
                                            <div className="d-flex" key={index}>
                                                <div className="col-12 col-sm-11 d-flex align-items-center">
                                                    <span className={`fs-8-1 me-2 ${duplicateIndexes.includes(index + 1) ? "text-danger" : ""}`}>{index + 1}.</span>
                                                    <div className='w-100'>
                                                        <FormInput showHeaderLabel={true}
                                                                   name = {`Questions[${index}].Content`}
                                                                   errors={errors}
                                                                   required={true}
                                                                   maxLengthText = "max length 500 character"
                                                                   label={"Assign Question"}
                                                                   validation={{...register(`Questions[${index}].Content`, {
                                                                           required: "Question is required."
                                                                       })}}
                                                                   disabled={editingIndex !== index}
                                                                   maxLength={500}
                                                                   className={editingIndex === index ? "border border-warning" : ""}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-12 col-sm-1 d-flex align-items-center">
                                                    {editingIndex === index ? (
                                                        <Fragment>
                                                            {/* SAVE */}
                                                            {
                                                                isSaveQuestion ?
                                                                    <Puff
                                                                        height="17"
                                                                        width="17"
                                                                        color="#54b4d3"
                                                                        ariaLabel="circles-loading"
                                                                        wrapperClass="ms-2"
                                                                    />
                                                                    :
                                                                    <FontAwesomeIcon
                                                                        icon={faSave}
                                                                        onClick={handleSaveQuestion}
                                                                        className="ms-2 cursor-pointer text-primary fs-8-1"
                                                                    />
                                                            }
                                                            {/* CANCEL */}
                                                            <FontAwesomeIcon
                                                                icon={faCancel}
                                                                onClick={() => handleCancelQuestion(index)}
                                                                className="ms-2 cursor-pointer fs-8-1"
                                                                style={{ color: "#cb3118" }}
                                                            />
                                                        </Fragment>
                                                    ) : (
                                                        <Fragment>
                                                            {/* EDIT */}
                                                            <FontAwesomeIcon
                                                                icon={faEdit}
                                                                onClick={() => handleEditQuestion(index)}
                                                                className="ms-2 cursor-pointer text-primary fs-8-1"
                                                            />
                                                            {/* DELETE */}
                                                            <FontAwesomeIcon
                                                                icon={faTrash}
                                                                onClick={() => handleRemoveQuestion(index)}
                                                                className="ms-2 cursor-pointer fs-8-1"
                                                                style={{ color: "#cb3118" }}
                                                            />
                                                        </Fragment>
                                                    )}
                                                </div>
                                            </div>
                                    )))
                                }
                                {
                                    mode !== "View" && (
                                        <div className="text-center">
                                            <button
                                                className="btn btn-bt-primary ms-4"
                                                type="button"
                                                onClick={handleAddQuestions}
                                            >
                                                Add New Question <Plus size={15}/>
                                            </button>
                                        </div>
                                    )
                                }
                            </div>
                            <div
                                className="tab-pane fade"
                                id="tab-answer"
                                role="tabpanel"
                                aria-labelledby="tab-answer-tab"
                            >
                                <div className="mb-3">
                                    <FormInput showHeaderLabel={true}
                                               name = {`DataChatbot.Content`}
                                               errors={errors}
                                               required={mode !== "View"}
                                               textarea={true}
                                               maxLengthText = "max length 5000 character"
                                               label="Assign Question"
                                               validation={{...register(`DataChatbot.Content`, {
                                                       required: "Answer is required."
                                                   })}}
                                               disabled={false}
                                               maxLength={5000}
                                               style={{height: "460px"}}
                                    />
                                </div>
                            </div>
                            <div className="tab-pane fade"
                                 id="tab-button"
                                 role="tabpanel"
                                 aria-labelledby="tab-button-tab"
                            >

                                { mode === "View" ? (
                                    // VIEW MODE
                                    buttonFields.map((item, index) => (
                                        <div key={index} className="mb-2">
                                            <p className="mb-1 fs-8-1"><strong>Text:</strong> {item.text}</p>
                                            <p className="mb-1 fs-8-1"><strong>Value:</strong> {item.value}</p>
                                            <hr/>
                                        </div>
                                    ))

                                ) : (
                                    <Fragment>
                                        {buttonFields.map((item, index) => (
                                            <div key={item.id} className="d-flex gap-3">
                                                <div className="col-12 col-sm-5">
                                                    <FormInput
                                                        name={`DataChatbot.ButtonLists[${index}].text`}
                                                        label="Button Text"
                                                        errors={errors}
                                                        validation={{...register(`DataChatbot.ButtonLists[${index}].text`, {
                                                                required: "text required"
                                                            })}}
                                                        disabled={false}
                                                        maxLength={500}
                                                        maxLengthText = "max length 500 character"
                                                    />
                                                </div>

                                                <div className="col-12 col-sm-5">
                                                    <FormInput
                                                        name={`DataChatbot.ButtonLists[${index}].value`}
                                                        label="Button Value"
                                                        errors={errors}
                                                        validation={{...register(`DataChatbot.ButtonLists[${index}].value`, {
                                                                required: "value required"
                                                            })}}
                                                        disabled={false}
                                                        maxLength={500}
                                                        maxLengthText = "max length 500 character"
                                                    />
                                                </div>

                                                <div className="col-12 col-sm-1 d-flex align-items-center">
                                                    <FontAwesomeIcon icon={faTrash}
                                                                     onClick={() => removeButton(index)}
                                                                     className="ms-2 cursor-pointer"
                                                                     style={{color: "#cb3118"}}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                        <button className="btn btn-bt-primary"
                                                type="button"
                                                onClick={handleAddButton}>
                                            Add Button <Plus size={15}/>
                                        </button>
                                    </Fragment>
                                )}
                            </div>
                        </div>
                    </div>
                </form>
            </CenteredModal>
            <CenteredModal
                title="Import Data"
                show={showImportModal}
                onClose={() => setShowImportModal(false)}
                formId="formImport"
                confirmText="Validate File"
            >
                <form onSubmit={fileHandleSubmit(onSubmitFormImport)} id="formImport">
                    <div className="mb-3">
                        <DropzoneFile
                            label="Import Json File"
                            register={fileRegister}
                            name="file"
                            errors={fileError}
                            requiredMessage="Json file is required."
                            selectedFile={selectedFile}
                            setSelectedFile={setSelectedFile}
                            imageFile={false}
                            maxSizeMB={100}
                            acceptedFormats={["application/json"]}
                        />
                    </div>
                    {
                        dataValidate.Succeeded && selectedFile && (
                            <Fragment>
                                <div className="text-center">
                                    <span className="fs-8-1 me-2"><strong>Total Qna: </strong> <span className="text-success">{dataValidate.TotalQna} </span></span>
                                    <span className="fs-8-1 me-2"><strong>Duplicate Qna: </strong> <span className="text-danger">{dataValidate.DuplicateQna} </span></span>
                                    <span className="fs-8-1"><strong>Available Qna: </strong> <span className="text-success"> {dataValidate.AvailableQna}</span></span>
                                </div>
                                <div className="text-center mt-3">
                                    {
                                        isImportingData ? (
                                            <p className="fs-8-1 fw-bold text-danger">
                                                We are importing data. Please wait a moment...
                                            </p>
                                        ) :
                                        (
                                            <button className="btn btn-bt-primary"
                                                    type="button"
                                                    disabled={dataValidate.AvailableQna <= 0}
                                                    onClick={handleConfirmImportData}>
                                                Import Data <Upload size={15} className="ms-2"/>
                                            </button>
                                        )
                                    }
                                </div>
                            </Fragment>
                        )
                    }
                </form>
            </CenteredModal>
            <CenteredModal
                title="Export Data"
                show={showExportConfirmModal}
                onClose={() => setShowExportConfirmModal(false)}
                confirmText="Confirm Export"
                onConfirm={confirmExportData}
            >
                <div className="text-center">
                    Are you sure you want to export your data?<br/>
                    <strong className="text-danger">This action will export ALL of your data</strong> <br/>
                    and <strong>may take some time to complete.</strong>
                </div>
            </CenteredModal>
            <CenteredModal
                title="Delete QnA"
                show={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                confirmText="Delete"
                onConfirm={confirmDeleteQnA}
            >
                <div className="text-center fs-8-1">
                    Are you sure you want to delete this data?<br/>
                    <strong className="text-danger">
                        This action cannot be undone.
                    </strong>
                </div>
            </CenteredModal>
            <CenteredModal
                title="Delete Duplicate Question"
                show={showDeleteDupModal}
                onClose={() => setShowDeleteDupModal(false)}
                confirmText="Confirm"
                onConfirm={confirmDeleteDuplicate}
            >
                <div className="text-center fs-8-1">
                    Are you sure you want to delete duplicate question for each answer?<br/>
                    <strong className="text-danger">
                        This action cannot be undone.
                    </strong>
                </div>
            </CenteredModal>
        </Fragment>
    )
}
export default DataChatbotManagement;